import ItemListColumnDefaultsJson from './item-list-column-defaults.json'

export interface ItemListColumnDefinition {
    id: string;
    label: string;
    labelShort: string;
    visible: boolean;
    order: number;
    minWidth: string;
    maxWidth: string | null;
    align: 'start' | 'center' | 'end';
    showSubColumn?: boolean;
}

export interface ItemListColumnPreferences {
    schema_version: 1;
    visibility: Record<string, boolean>;
    order: string[] | null;
    widths?: Record<string, number>;
}

// 렌더링용 컬럼 배열의 기존 이름을 유지한다.
export type ItemListColumnConfig = ItemListColumnDefinition[];

export const ItemListColumnDefaults: ItemListColumnDefinition[] = ItemListColumnDefaultsJson as ItemListColumnDefinition[];

export const ItemListColumnPreferencesDefault: ItemListColumnPreferences = {
    schema_version: 1,
    visibility: {
        'col-lowest-price': true,
        'col-item-price-reset': true,
        'col-active-schedule-count': true,
    },
    order: [
        'col-result-number',
        'col-sort-order',
        'col-active-status',
        'col-item-name',
        'col-item-code',
        'col-keep-default-price',
        'col-item-price-reset',
        'col-market',
        'col-item-price',
        'col-item-discount-amount',
        'col-item-discount-percent',
        'col-compare-site',
        'col-compare-rank',
        'col-lowest-price',
        'col-adjust-gap',
        'col-adjust-strategy',
        'col-adjust-lower-limit',
        'col-coupon-discount',
        'col-coupon-percent',
        'col-ranked-price',
        'col-exposed-price',
        'col-price-change',
        'col-error-count',
        'col-active-schedule-count',
        'col-processed-at',
    ],
    widths: {},
};

export const createItemListColumnPreferences = (): ItemListColumnPreferences => ({
    ...ItemListColumnPreferencesDefault,
    visibility: { ...ItemListColumnPreferencesDefault.visibility },
    order: ItemListColumnPreferencesDefault.order
        ? [...ItemListColumnPreferencesDefault.order]
        : null,
    widths: {},
});

export type ItemListColumnId = string;

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const normalizeItemListColumnPreferences = (rawPreferences: unknown): ItemListColumnPreferences => {
    if (!isRecord(rawPreferences)) {
        return createItemListColumnPreferences();
    }

    const { schema_version, visibility, order, widths } = rawPreferences;
    if (schema_version !== 1 || !isRecord(visibility)) {
        return createItemListColumnPreferences();
    }

    const visibilityEntries = Object.entries(visibility);
    if (visibilityEntries.some(([, value]) => typeof value !== 'boolean')) {
        return createItemListColumnPreferences();
    }

    if (order !== null && (
        !Array.isArray(order)
        || order.some((columnId) => typeof columnId !== 'string')
        || new Set(order).size !== order.length
    )) {
        return createItemListColumnPreferences();
    }

    if (widths !== undefined && (
        !isRecord(widths)
        || Object.values(widths).some((width) =>
            typeof width !== 'number' || !Number.isInteger(width) || width < 32 || width > 1000)
    )) {
        return createItemListColumnPreferences();
    }

    return {
        schema_version: 1,
        visibility: Object.fromEntries(visibilityEntries) as Record<string, boolean>,
        order: order === null ? null : [...order],
        widths: widths === undefined ? {} : { ...widths } as Record<string, number>,
    };
};

const getOrderedUniqueDefinitions = (
    definitions: ItemListColumnDefinition[],
): ItemListColumnDefinition[] => {
    const seenIds = new Set<string>();
    return [...definitions]
        .sort((a, b) => a.order - b.order)
        .filter((definition) => {
            if (seenIds.has(definition.id)) {
                return false;
            }
            seenIds.add(definition.id);
            return true;
        });
};

export const mergeItemListColumnOrder = (
    definitions: ItemListColumnDefinition[],
    savedOrder: string[] | null,
): string[] => {
    const orderedDefinitions = getOrderedUniqueDefinitions(definitions);
    const defaultIds = orderedDefinitions.map((definition) => definition.id);

    if (savedOrder === null) {
        return defaultIds;
    }

    const knownIds = new Set(defaultIds);
    const result: string[] = [];
    const resultIds = new Set<string>();

    savedOrder.forEach((columnId) => {
        if (knownIds.has(columnId) && !resultIds.has(columnId)) {
            result.push(columnId);
            resultIds.add(columnId);
        }
    });

    defaultIds.forEach((columnId, index) => {
        if (resultIds.has(columnId)) {
            return;
        }

        const nextDefaultId = defaultIds
            .slice(index + 1)
            .find((nextId) => resultIds.has(nextId));
        const insertIndex = nextDefaultId === undefined
            ? result.length
            : result.indexOf(nextDefaultId);

        result.splice(insertIndex < 0 ? result.length : insertIndex, 0, columnId);
        resultIds.add(columnId);
    });

    // 구버전 사용자 설정에는 고정 컬럼이었던 순번/정렬 ID가 없다.
    // 두 컬럼만 최신 기본 위치인 맨 앞에 넣고 기존 사용자 순서는 유지한다.
    const leadingColumnIds = ['col-result-number', 'col-sort-order']
        .filter((columnId) => knownIds.has(columnId) && !savedOrder.includes(columnId));
    if (leadingColumnIds.length < 1) {
        return result;
    }
    const leadingIdSet = new Set(leadingColumnIds);
    return [
        ...leadingColumnIds,
        ...result.filter((columnId) => !leadingIdSet.has(columnId)),
    ];
};

export const resolveItemListColumnConfig = (
    preferences: ItemListColumnPreferences,
    definitions: ItemListColumnDefinition[] = ItemListColumnDefaults,
): ItemListColumnConfig => {
    const orderedDefinitions = getOrderedUniqueDefinitions(definitions);
    const definitionsById = new Map(orderedDefinitions.map((definition) => [definition.id, definition]));
    const orderedIds = mergeItemListColumnOrder(orderedDefinitions, preferences.order);

    return orderedIds.map((columnId, index) => {
        const definition = definitionsById.get(columnId)!;
        const visibilityOverride = preferences.visibility[columnId];
        const widthOverride = preferences.widths?.[columnId];
        return {
            ...definition,
            visible: typeof visibilityOverride === 'boolean' ? visibilityOverride : definition.visible,
            order: index + 1,
            ...(widthOverride === undefined ? {} : {
                minWidth: `${widthOverride}px`,
                maxWidth: `${widthOverride}px`,
            }),
        };
    });
};

export const updateItemListColumnVisibility = (
    preferences: ItemListColumnPreferences,
    columnId: string,
    visible: boolean,
    definitions: ItemListColumnDefinition[] = ItemListColumnDefaults,
): ItemListColumnPreferences => {
    const defaultColumn = definitions.find((column) => column.id === columnId);
    const visibility = { ...preferences.visibility };

    if (defaultColumn && defaultColumn.visible === visible) {
        delete visibility[columnId];
    } else {
        visibility[columnId] = visible;
    }

    return {
        ...preferences,
        visibility,
    };
};

export const updateItemListColumnOrder = (
    preferences: ItemListColumnPreferences,
    orderedColumnIds: string[],
): ItemListColumnPreferences => ({
    ...preferences,
    order: [...orderedColumnIds],
});

export const resetItemListColumnOrder = (
    preferences: ItemListColumnPreferences,
): ItemListColumnPreferences => ({
    ...preferences,
    order: createItemListColumnPreferences().order,
});

export const resetItemListColumnWidths = (
    preferences: ItemListColumnPreferences,
): ItemListColumnPreferences => ({
    ...preferences,
    widths: {},
});

// 배열을 order 기준으로 정렬된 배열로 반환
export const getItemListColumnsAsArray = (config?: ItemListColumnConfig): ItemListColumnConfig => {
    const configArray = config ?? ItemListColumnDefaults;
    return [...configArray].sort((a, b) => a.order - b.order);
};

// 배열을 order 기준으로 정렬한 entries 반환 (id, item 튜플)
export const getSortedColumnEntries = (config: ItemListColumnConfig) => {
    return config
        .map((item) => [item.id, item] as const)
        .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999));
};
